import { CustomEventPermission, Project, INGESTION_POLICIES, MergedCustomEvent } from '../types';
import { DEACTIVATED_PERMISSION, DISCARDED_PERMISSION, SAVED_PERMISSION } from '../constants';

export const addPermission = (
  eventName: string,
  customEventPermissions: CustomEventPermission[],
): CustomEventPermission[] => [
  ...customEventPermissions,
  { name: eventName, allowed: true, saved: true },
];

export const updatePermission = (
  eventName: string,
  PERMISSION: { allowed: boolean; saved: boolean },
  project: Project,
) => {
  let exists = false;
  const existingPermissions = project?.extendedProjectSettings?.customEventPermissions.map(
    (permission) => {
      if (permission.name === eventName) {
        exists = true;
        return { ...permission, ...PERMISSION };
      }
      return permission;
    },
  );
  return exists
    ? existingPermissions
    : [...existingPermissions, { ...PERMISSION, name: eventName }];
};

export const createPermissionsPayload = (
  eventName: string,
  PERMISSION: { allowed: boolean; saved: boolean },
  project: Project,
) => ({
  projectId: project.id,
  allowNewCustomEvents: project.extendedProjectSettings.allowNewCustomEvents,
  customEventPermissions: updatePermission(eventName, PERMISSION, project),
});

export const createAddEventPayload = (eventName: string, project: Project) => ({
  projectId: project.id,
  allowNewCustomEvents: project?.extendedProjectSettings?.allowNewCustomEvents,
  customEventPermissions: addPermission(
    eventName,
    project?.extendedProjectSettings?.customEventPermissions,
  ),
});

export const createBlockAllowPayload = (allowNewCustomEvents: boolean, project: Project) => ({
  projectId: project.id,
  allowNewCustomEvents,
  customEventPermissions: project.extendedProjectSettings.customEventPermissions,
});

/**
    The fields of "allowed" and "saved" reflect the original conception of
    custom event ingestion management. "Saved" specifies whether new events of
    this type will be ingested or not. "Allowed" specifies whether the event is
    visible in the UI (ie. selectable in workflows). The fact that these are two
    separate fields in the UI however suggests that there are 4 possible
    configurations, which is not the case -- an event cannot be "saved" unless
    it is also "allowed".

    Besides being a bit clearer, the updated terms of "saved", "discarded",
    and "deactivated" more closely reflect the 3 ingestion policies actually
    configurable, and the below functions execute that mapping in each
    direction.
  */

export const configuredIngestionPolicy = (event: MergedCustomEvent) => {
  const { allowed, saved } = event;
  if (allowed && saved) return INGESTION_POLICIES.SAVED;
  if (allowed) return INGESTION_POLICIES.DISCARDED;
  return INGESTION_POLICIES.DEACTIVATED;
};

export const customEventPermission = (ingestionPolicy: INGESTION_POLICIES) => {
  switch (ingestionPolicy) {
    case INGESTION_POLICIES.SAVED:
      return SAVED_PERMISSION;
    case INGESTION_POLICIES.DISCARDED:
      return DISCARDED_PERMISSION;
    case INGESTION_POLICIES.DEACTIVATED:
    default:
      return DEACTIVATED_PERMISSION;
  }
};
